import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Stack, Typography, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import { useDispatch, useSelector, useLocale } from 'hooks';
import { useI18n } from 'locales';
import { LocaleLink } from 'components/link';
import { shallowEqual } from 'react-redux';
import get from 'lodash/get';
import thousands from 'thousands';
import * as Actions from '@/redux/actions';
import PortalHeader from '../../components/PortalHeader';
import { Button } from 'components/button';

const selector = (state) => ({
  givingInfo: state.givingInfo,
});

function GiveFailPage() {
  const { givingInfo } = useSelector(selector, shallowEqual);
  const dispatch = useDispatch();
  const t = useI18n();
  const locale = useLocale();
  const [searchParams] = useSearchParams();
  const givingId = searchParams.get('id');
  const giving = get(givingInfo, 'data', {});

  useEffect(() => {
    if (givingId) {
      dispatch(Actions.readGiving({ payload: { id: givingId } }));
    }
  }, [dispatch, givingId]);

  const isLoading = givingInfo.isFetching;

  if (isLoading && !givingInfo.dataFetched) {
    return (
      <Stack sx={{
        backgroundColor: 'background.default',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
      }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack sx={{
      backgroundColor: 'background.default',
      pb: 8,
    }}>

      <PortalHeader header={t('GIVING')} />

      <Stack gap={2} sx={{ px: 2, py: 3, alignItems: 'center' }}>
        <Typography color='error.main' sx={{ fontSize: 120 }}>
          <FontAwesomeIcon icon={faCircleXmark} />
        </Typography>

        <Typography variant='h4' color='secondary' sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          {t('PAYMENT FAILED')}
        </Typography>

        <Stack gap={0.5}>
          <Typography variant='body1' color='info.700' sx={{ textAlign: 'center' }}>
            {t('Unfortunately, your payment for the giving to')} {get(giving, 'fund.nameEng', '[Fund Name]')} {t('could not be processed')}.
          </Typography>
          <Typography variant='body1' color='info.700' sx={{ textAlign: 'center' }}>
            {t('Amount')}: HKD {thousands((giving.amount || 0).toFixed(2))}
          </Typography>
          <Typography variant='body1' color='info.700' sx={{ textAlign: 'center' }}>
            {t('Please check your payment details and try again')}.
          </Typography>
        </Stack>

        <Stack gap={2} sx={{ mt: 3, alignItems: 'center' }}>
          <LocaleLink to={`/give?locale=${locale}`} style={{ textDecoration: 'none' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem',
                px: 4,
                py: 1.5,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'primary.dark'
                }
              }}
            >
              {t('TRY AGAIN')}
            </Button>
          </LocaleLink>

          <Typography variant='body2' color='info.700' sx={{ textAlign: 'center' }}>
            {t('Need help')}?
            <a href="mailto:info@standrews.org.hk" style={{ color: '#00B4AA', marginLeft: 4 }}>
              {t('Contact Support')}
            </a>
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default GiveFailPage;
